import StaticPage from '@/templates/pages/StaticPage';
import Footer from '@/Layouts/Footer/Footer';
import Header from '@/Layouts/Header/Header';
import Teaser from '@/components/teaser/Teaser';
import Accordion from '@/components/accordion/Accordion';
import Image from '@/components/image/Image';
import Headline from '@/components/Headline';
import RichText from '@/components/RichText/RichText';
import AccordionElement from '@/components/accordion/AccordionElement';
import Tabs from '@/components/tabs/Tabs';
import SimpleHeaderText from '@/components/SimpleHeaderText';
import FixedColumnLayout from '@/components/fixedColumnLayout/FixedColumnLayout';
import LeftNavi from '@/components/leftNavi/LeftNavi';
import IbeWrapper from '@/components/ibe/IbeWrapper';
import IbeSearch from '@/components/ibe/ibeSearch/IbeSearch';
import FeatureList from '@/components/featureList/FeatureList';
import Checkout from '@/components/checkout/Checkout';
import LinkList from '@/components/linkList/LinkList';
import Link from '@/components/linkList/Link';
import Poster from '@/components/poster/Poster';
import Button from '@/components/button/Button';
import Quotes from '@/components/quotes/Quotes';
import ConsentButton from '@/components/consentButton/ConsentButton';
import AlertBox from '@/components/alertBox/AlertBox';
import BookingList from '@/components/bookingList/BookingList';
import BookingDetails from '@/components/booking/BookingDetails';
import Offer from '@/components/offer/Offer';
import Newsletter from '@/components/newsletter/Newsletter';
import OfferFixedColumnLayout from '@/components/offer/OfferFixedColumnLayout';
import PasswordChange from '@/components/password/PasswordChange';
import { ApiBreadCrumbResponse, ApiPage } from '../../api';
import YsnConfigModel from '@/Config/YsnConfigModel';
import { Hotel } from '@/types/cms/Hotel';
import { Destination } from '@/types/cms/Destination';
import Destinations from '@/components/Destinations/Destinations';
import HotelInfo from '@/components/hotel/HotelInfo';
import {
  ComponentProps as MgnlComponentProps,
  IMagnoliaContext,
  MgnlContent,
  MgnlTemplateAnnotations
} from '@magnolia/frontend-helpers-base';
import { MagnoliaConfig } from '@magnolia/react-editor';
import { getCacheHeaders } from '@/Util/api';

export const PUBLIC_CONFIG_NODE_PATH = '/fe-public-config';

const allowedFetchQueryParams = ['host', 'path', 'lang'];

export function apiFetch(
  url: string,
  init?: RequestInit,
  isAuthor?: boolean,
  fetchTTL?: string
): ReturnType<typeof fetch> {
  const parsedUrl = new URL(url);
  const originalSearchParams = parsedUrl.searchParams;
  const filteredSearchParams = new URLSearchParams();

  for (const [key, value] of originalSearchParams.entries()) {
    if (allowedFetchQueryParams.includes(key)) {
      filteredSearchParams.append(key, value);
    }
  }
  parsedUrl.search = filteredSearchParams.toString();
  const finalUrl = parsedUrl.toString();

  if (!process.env.MAGNOLIA_AUTH_USER || !process.env.MAGNOLIA_AUTH_PASSWORD) {
    return fetch(finalUrl, {
      ...(init || {}),
      ...getCacheHeaders(isAuthor, undefined, fetchTTL)
    });
  }
  const credentials = Buffer.from(
    process.env.MAGNOLIA_AUTH_USER + ':' + process.env.MAGNOLIA_AUTH_PASSWORD
  ).toString('base64');
  return fetch(finalUrl, {
    ...(init || {}),
    headers: Object.assign(init?.headers ?? {}, {
      Authorization: `Basic ${credentials}`
    }),
    ...getCacheHeaders(isAuthor, undefined, fetchTTL)
  });
}

export const rootNodePath: string = process.env.MAGNOLIA_SITE_PATH ?? '';

interface ErrorMsg {
  code: string;
  message: string;
}

export interface MagnoliaNode {
  '@name': string;
  '@path': string;
  '@id': string;
  '@nodeType': string;
  '@nodes': { [key: string]: string };
  '@index'?: number;
}

export interface DefaultPageProps {
  // areas
  header: MgnlContent;
  top: MgnlContent;
  main: MgnlContent;
  footer: MgnlContent;
  pageProps: PageProps;
}

export type RouteParams = {
  domain: string;
  lang?: string;
  cmspath?: string[];
};

export interface MagnoliaPageNode extends MgnlContent {
  'mgnl:created': string;
  'mgnl:lastModified': string;
  'mgnl:template': string;
  error?: ErrorMsg;
  title: string;
  metaTitle?: string;
  metaDescription?: string;
  robots: string[] | MetaRobots;
  socialMediaTitle?: string;
  socialMediaDescription?: string;
  socialMediaImage?: MagnoliaDamNode;
}

export interface MagnoliaComponentNode extends MagnoliaPageNode {}

export interface MagnoliaDamNode extends MagnoliaNode {
  '@link': string;
  metadata: {
    fileName: string;
    fileSize: number;
    format: string;
    mimeType: string;
    width: number;
    height: number;
    title?: string;
    caption?: string;
  };
  renditions?: {
    [rendition: string]: {
      mimeType: string;
      link: string;
    };
  };
}

export type LinkSwitchableMixin = {
  // TODO: find a better way for switchable fields
  linkType: 'page' | 'external' | 'download';
  linkTypepage?: string;
  linkTypedownload?: {
    '@name': string;
    '@path': string;
    '@id': string;
    '@link': string;
  };
  linkTypeexternal?: string;
};

export type customImageSizeSwitchableMixin = {
  imageSize: 'original' | 'thumbnail' | 'small' | 'medium' | 'large' | 'square' | 'custom';
  // CamelCase is not working here due to the naming in CMS, same for LinkSwitchableMixin
  imageSizecustomImageWidth: number;
  imageSizecustomImageHeight: number;
};

export interface MagnoliaMappingConfig {
  componentMappings: { [key: string]: React.FC<any> };
}

// IBE Components
export const ibeSearchComponent = 'website:components/ibesearch';
export const ibeComponent = 'website:components/ibe';
export const checkoutComponent = 'website:components/checkout';
export const offerComponent = 'website:components/offer';

export const config: MagnoliaConfig = {
  componentMappings: {
    'website:pages/static': StaticPage,

    'website:components/headline': Headline,
    'website:components/richText': RichText,
    'website:components/image': Image,
    'website:components/footer': Footer,
    'website:components/header': Header,
    'website:components/accordion': Accordion,
    'website:components/accordionElement': AccordionElement,
    'website:components/tabs': Tabs,
    'website:components/simpleHeaderText': SimpleHeaderText,
    'website:components/fixedColumnLayout': FixedColumnLayout,
    'website:components/leftNavi': LeftNavi,
    'website:components/featureList': FeatureList,
    'website:components/teaser': Teaser,
    'website:components/link': Link,
    'website:components/linkList': LinkList,
    'website:components/poster': Poster,
    'website:components/button': Button,
    'website:components/quotes': Quotes,
    'website:components/consentButton': ConsentButton,
    'website:components/alertBox': AlertBox,
    'website:components/bookingList': BookingList,
    'website:components/bookingDetails': BookingDetails,
    [offerComponent]: Offer,
    'website:components/offerFixedColumnLayout': OfferFixedColumnLayout,
    'website:components/newsletter': Newsletter,
    'website:components/passwordChange': PasswordChange,
    [ibeComponent]: IbeWrapper,
    [ibeSearchComponent]: IbeSearch,
    [checkoutComponent]: Checkout,
    'website:components/destinations': Destinations,
    'website:components/hotelInfo': HotelInfo
  }
};

export type MetaRobots = {
  index: boolean;
  follow: boolean;
  noindex: boolean;
  nofollow: boolean;
};

export interface PageProps {
  magnoliaContext: IMagnoliaContext;
  magnoliaHost: string;
  magnoliaHostClient: string;
  rootNodePath: string;
  page: MagnoliaPageNode;
  headerNav: ApiPage[];
  locale: string;
  templateAnnotations?: MgnlTemplateAnnotations;
  config: MGNLConfigType;
  hasIbeComponent: boolean;
  hasCheckoutComponent: boolean;
  breadCrumb: ApiBreadCrumbResponse['nodes'];
  cmsHotels: Hotel[];
  cmsDestinations: Destination[];
  fetchTTL?: string;
  isAuthor?: boolean;
  translationVersion: string;
}

export type ComponentProps = MgnlComponentProps & { pageProps: PageProps };

export type MGNLConfigType = MagnoliaNode & Partial<YsnConfigModel>;

export interface UriMapping {
  url: string;
  statusCode?: number;
}

export interface MagnoliaSite {
  name: string;
  enabled: boolean;
  parameters: SiteConfigParameters;
  i18n: {
    locale: string;
    locales: string[];
    enabled: boolean;
    defaultLocale: string;
    fallbackLocale: string;
  };
  mappings: {
    [key: string]: {
      repository: string;
      handlePrefix: string;
      uriprefix: string;
    };
  };
}

export const magnoliaPath = '/.magnolia/admincentral';

export type SiteConfigParameters = {
  uriMapping?: UriMapping;
};
